'use client';

/* ──────────────────────────────────────────────
 *  PaymentReassurance — "secure payment" badge +
 *  accepted payment-method logos, shown in the
 *  booking, ride and gift-card tunnels. Shows only
 *  the platform-supported methods (the single list
 *  the checkout also charges from). Brand logos are
 *  vendored SVGs (see ./payment-logos).
 * ────────────────────────────────────────────── */

import { JSX } from 'react';
import { isSupportedPaymentMethod } from '@cfixe-monorepo/shared';
import { TOKEN_TO_LOGOS, paymentLogoDataUri, type PaymentLogo } from './payment-logos';

interface PaymentReassuranceProps {
  /**
   * Stripe payment-method tokens to show: the intent's own `payment_method_types` at payment, the
   * backend-resolved `allowed_payment_methods` before (plan, country, currency — rule 39 §3).
   */
  methods?: string[];
  /** Label for the secure-payment badge (e.g. "Paiement sécurisé"). */
  secureLabel: string;
  /** Label introducing the accepted methods (e.g. "Moyens de paiement acceptés"). */
  methodsLabel: string;
  /**
   * Visual layout:
   * - "stacked" (default): vertical block with the methods label — used in the checkout payment section.
   * - "inline": single compact horizontal row (lock + label + brand logos), no methods label —
   *   used by the persistent trust bar shown throughout the tunnel.
   */
  layout?: 'stacked' | 'inline';
}

export function PaymentReassurance({
  methods,
  secureLabel,
  methodsLabel,
  layout = 'stacked',
}: PaymentReassuranceProps): JSX.Element {
  /* Safety net: only the platform-supported methods (SUPPORTED_PAYMENT_METHOD_TOKENS, @cfixe/shared)
     have a logo — a logo must never announce a method the PaymentIntent will not offer. */
  const logos = (methods ?? []).flatMap((token): PaymentLogo[] =>
    isSupportedPaymentMethod(token) ? TOKEN_TO_LOGOS[token] : [],
  );
  const isInline = layout === 'inline';
  const rootClassName = isInline ? 'bt-payment-reassurance bt-payment-reassurance--inline' : 'bt-payment-reassurance';

  return (
    <div className={rootClassName}>
      <div className="bt-payment-reassurance__secure">
        <LockIcon />
        <span>{secureLabel}</span>
      </div>
      {logos.length > 0 && (
        <div className="bt-payment-reassurance__methods">
          {!isInline && <span className="bt-payment-reassurance__methods-label">{methodsLabel}</span>}
          <div className="bt-payment-reassurance__icons">
            {logos.map(
              (logo): JSX.Element => (
                <img
                  key={logo.key}
                  className="bt-payment-reassurance__brand"
                  src={paymentLogoDataUri(logo.svg)}
                  alt={logo.label}
                  loading="lazy"
                />
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Secure-badge lock (inline, inherits text color) ───────────────── */

function LockIcon(): JSX.Element {
  return (
    <svg
      className="bt-payment-reassurance__lock"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
